import { useEffect, useMemo, useState } from 'react';

import {
	INVITED_APP_SHORTCUTS_ALLOWED_TARGET_APPS,
	type InvitedAppShortcutAllowedTargetAppKey,
} from '../../common/types';
import { getCollaboratorsInProducts } from '../collaborators-in-products';

import { useInvitedAppAnalytics } from './invited-app-analytics';
import {
	type ExperimentDetails,
	type FullContext,
	IneligibilityReason,
	type Traits,
} from './types';
import {
	type AccessMap,
	type GetAppAccessByShortcutsInput,
	getAppAccessFromProductLinksFilter,
	getAppAccessFromShortcuts,
	type GetAppAccessInput,
	hasAccessToAnyTargetProduct,
	ineligible,
	isFirstActiveExpiringSoonNeedsRefresh,
	type ResolvedCacheDetails,
	resolveExperimentDetails,
	resolveFromCache,
	updateActivation,
	updateCachedEligibility,
	updateCollaborators,
	updateFirstImpression,
} from './utils/invited-app-shortcuts-resolver';
import { useCanWriteToCache } from './utils/use-can-write-to-cache';

export type UseInvitedAppShortcutsInput =
	| ({ enabled: false } & Partial<UseInvitedAppShortcutsEnabledInput>)
	| ({ enabled: true } & UseInvitedAppShortcutsEnabledInput);

export type UseInvitedAppShortcutsEnabledInput = FullContext & {
	onCacheNeedsRefresh?: () => void;
} &
	// Either provide traits directly...
	(| {
		fetchTraits?: undefined;
		fetchTraitsPolicy?: undefined;
		traits: Traits | null;
	}
		// ...or means to fetch them:
		| {
			fetchTraits: TraitsFetcher;
			fetchTraitsPolicy: 'always-fetch' | 'prefer-cache';
			traits?: undefined;
		}
	);

export type UseInvitedAppShortcutsResult = ExperimentDetails & {
	fireFeatureExposed: (input: { isEnabled: boolean }) => void;
	registerActivation: (product: InvitedAppShortcutAllowedTargetAppKey) => void;
	registerImpression: (input?: { products?: InvitedAppShortcutAllowedTargetAppKey[] }) => void;
	updateFromAppAccessFilter: (input: GetAppAccessInput) => void;
	updateFromHydratedShortcuts: (input: GetAppAccessByShortcutsInput) => void;
};

type TraitsFetcher = ({ cloudId, userId }: { cloudId: string; userId: string }) => Promise<Traits>;

type TraitsState =
	| {
		status: 'complete';
		traits: Traits;
	}
	| {
		status: 'loading' | 'initial';
		traits?: undefined;
	}
	| {
		error: Error;
		status: 'error';
		traits?: undefined;
	};

export const useInvitedAppShortcuts = ({
	enabled,
	sourceProduct,
	targetProducts,
	cloudId,
	userId,
	onCacheNeedsRefresh,
	traits: traitsArg,
	fetchTraits,
	fetchTraitsPolicy,
	cache,
}: UseInvitedAppShortcutsInput): UseInvitedAppShortcutsResult => {
	const {
		fireInvitedAppShortcutsEligibilityChecked,
		fireFeatureExposed,
		fireFetchInvitedAppShortcutsTraitsSucceeded,
		fireFetchInvitedAppShortcutsTraitsFailed,
		fireFetchCollaboratorAccessSucceeded,
		fireFetchCollaboratorAccessFailed,
		fireEligibilityUpdateSucceeded,
		fireEligibilityUpdateFailed,
	} = useInvitedAppAnalytics();
	// TODO: add option that would pre-filter the app items that might be displayed in the menu
	// (based e.g. on license info or menu configuration)
	const canWriteToCache = useCanWriteToCache(enabled ? { enabled, cache } : { enabled: false });
	const [traitsState, setTraitsState] = useState<TraitsState>(() =>
		traitsArg ? { status: 'complete', traits: traitsArg } : { status: 'initial' },
	);
	const [eligibleAccess, setEligibleAccess] = useState<AccessMap | null>(null);
	const [cached] = useState<ResolvedCacheDetails>(() => {
		if (enabled) {
			try {
				return resolveFromCache({ sourceProduct, targetProducts, cache });

				// eslint-disable-next-line no-unused-vars
			} catch (_ignoredError) {
				return { eligibility: ineligible(IneligibilityReason.ERROR) };
			}
		} else {
			return { eligibility: ineligible(IneligibilityReason.EXPERIMENT_OFF) };
		}
	});

	// Fetch traits
	// if we've already established that user has eligible access to target products
	useEffect(() => {
		(async function maybeFetchTraits() {
			if (
				enabled &&
				userId &&
				canWriteToCache &&
				eligibleAccess &&
				traitsState.status === 'initial'
			) {
				const cacheNeedsRefresh = doesCacheNeedRefresh();
				if (cacheNeedsRefresh) {
					onCacheNeedsRefresh?.();
				}
				if (fetchTraits && (fetchTraitsPolicy === 'always-fetch' || cacheNeedsRefresh)) {
					setTraitsState({ status: 'loading' });
					try {
						const traits = await fetchTraits({ cloudId, userId });
						setTraitsState({ status: 'complete', traits });
						fireFetchInvitedAppShortcutsTraitsSucceeded({
							sourceApp: sourceProduct,
							targetApps: targetProducts,
						});
					} catch (error) {
						setTraitsState({ status: 'error', error: error as Error });
						fireFetchInvitedAppShortcutsTraitsFailed({
							sourceApp: sourceProduct,
							targetApps: targetProducts,
						});
					}
				}
			}
		})();

		function doesCacheNeedRefresh() {
			return (
				cached.eligibility.ineligibilityReason === IneligibilityReason.CACHE_EMPTY ||
				cached.eligibility.ineligibilityReason === IneligibilityReason.CACHE_EXPIRED ||
				isFirstActiveExpiringSoonNeedsRefresh(cached.state)
			);
		}
	}, [
		cached.eligibility,
		cached.state,
		canWriteToCache,
		cloudId,
		userId,
		enabled,
		eligibleAccess,
		fetchTraits,
		fetchTraitsPolicy,
		onCacheNeedsRefresh,
		traitsArg,
		traitsState.status,
		fireFetchInvitedAppShortcutsTraitsSucceeded,
		fireFetchInvitedAppShortcutsTraitsFailed,
		sourceProduct,
		targetProducts,
	]);

	// Synchronize traits state with the supplied traits arg
	useEffect(() => {
		if (enabled && traitsState.status !== 'complete' && traitsArg) {
			setTraitsState({ status: 'complete', traits: traitsArg });
		}
	}, [enabled, traitsArg, traitsState.status]);

	// Update state once we got all the data to update eligibility
	useEffect(() => {
		(async function mainEligibilityUpdate() {
			if (enabled && userId && traitsState.status === 'complete' && eligibleAccess) {
				try {
					const updatedCacheState = await updateCachedEligibility({
						traits: traitsState.traits,
						access: eligibleAccess,
						cache,
					});
					const updatedEligibility = resolveExperimentDetails(
						{ sourceProduct, targetProducts },
						updatedCacheState,
					);
					const willNowBeEligible = updatedEligibility.ineligibilityReason === null;
					if (willNowBeEligible) {
						const products = INVITED_APP_SHORTCUTS_ALLOWED_TARGET_APPS;
						const collaboratorsAccess = await getCollaboratorsInProducts({
							products,
							cloudId,
							userId,
						}).then((collaboratorsAccess) => {
							fireFetchCollaboratorAccessSucceeded({
								sourceApp: sourceProduct,
								targetApps: targetProducts,
								collaborators: collaboratorsAccess.length,
							});
							return collaboratorsAccess;
						}).catch((error) => {
							fireFetchCollaboratorAccessFailed({
								sourceApp: sourceProduct,
								targetApps: targetProducts,
								error: error instanceof Error ? error.message : `${error}`,
							});
							// fallback to empty collaborators access rather than failing the whole process
							return [];
						});

						updateCollaborators({
							collaboratorsWithProducts: collaboratorsAccess,
							products,
							cache,
						});
					}

					fireEligibilityUpdateSucceeded({
						sourceApp: sourceProduct,
						targetApps: targetProducts,
					});
				} catch (error: unknown) {
					fireEligibilityUpdateFailed({
						sourceApp: sourceProduct,
						targetApps: targetProducts,
						error: error instanceof Error ? error.message : `${error}`,
					});
				}
			}
		})();
	}, [cache, enabled, eligibleAccess, traitsState, userId, cloudId, sourceProduct, targetProducts, fireFetchCollaboratorAccessSucceeded, fireFetchCollaboratorAccessFailed, fireEligibilityUpdateSucceeded, fireEligibilityUpdateFailed]);

	useEffect(() => {
		if (cached.eligibility.ineligibilityReason) {
			fireInvitedAppShortcutsEligibilityChecked({
				ineligibilityReason: cached.eligibility.ineligibilityReason,
			});
		}
	}, [cached.eligibility.ineligibilityReason, fireInvitedAppShortcutsEligibilityChecked]);

	return useMemo(() => {
		const getEligibleInvitedApps = () =>
			(
				Object.keys(cached.eligibility.invitedApps || {}) as InvitedAppShortcutAllowedTargetAppKey[]
			).filter((appName) => cached.eligibility.invitedApps?.[appName]?.eligible);

		return {
			...cached.eligibility,

			/**
			 * Call this function when BFF yields a new version of the `getFilteredProductLinks` function.
			 *
			 * It will update our eligibility cache based on app access info from the BFF,
			 * using that filter function.
			 */
			updateFromAppAccessFilter: ({
				getFilteredProductLinks,
				limitToOnlyApsKeys = null,
			}: GetAppAccessInput) => {
				if (enabled && !eligibleAccess) {
					const access = getAppAccessFromProductLinksFilter({
						getFilteredProductLinks,
						limitToOnlyApsKeys,
					});
					if (hasAccessToAnyTargetProduct(access, targetProducts)) {
						setEligibleAccess(access);
					}
				}
			},

			updateFromHydratedShortcuts: ({
				shortcuts,
				limitToOnlyApsKeys,
			}: GetAppAccessByShortcutsInput) => {
				if (enabled && !eligibleAccess) {
					const access = getAppAccessFromShortcuts({ shortcuts, limitToOnlyApsKeys });
					if (hasAccessToAnyTargetProduct(access, targetProducts)) {
						setEligibleAccess(access);
					}
				}
			},

			registerImpression: ({ products = getEligibleInvitedApps() } = {}) => {
				if (enabled) {
					updateFirstImpression({ productsSeen: products, cache });
				}
			},

			registerActivation: (product: InvitedAppShortcutAllowedTargetAppKey) => {
				if (enabled) {
					updateActivation({ product, cache });
				}
			},

			fireFeatureExposed,
		};
	}, [cache, cached.eligibility, enabled, targetProducts, eligibleAccess, fireFeatureExposed]);
};
